import { createRef,forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import gsap from 'gsap';
import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { Flex, MEDIA_QUERY } from '@bucketplace/design-system';

import { AnimationComponentRef } from 'brand/hooks';

import { LIFE_EVENT_IMAGE_SIZE, LIFE_EVENT_IMAGES_REVERSE, LIFE_EVENT_TEXT } from '../constants';
import { Container, ScrollControl } from '../RebrandSectionContainer';
import { getRebrandStyle } from '../utils/style';
import { Card } from './Card';

const TEXT_OPACITY = {
  ACTIVE: 1,
  INACTIVE: 0.1,
};

const Y_OFFSET = 20;

export const LifeEventSection = forwardRef<AnimationComponentRef>((_, ref) => {
  const cardsLength = LIFE_EVENT_IMAGES_REVERSE.length;

  const containerRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const textRefs = useRef(
    Array.from({ length: LIFE_EVENT_TEXT.length }, () =>
      createRef<HTMLParagraphElement>()
    )
  );
  /** 카드 */
  const cardContainerRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef(
    Array.from({ length: cardsLength }, () =>
      createRef<HTMLDivElement>()
    )
  );
  const scrollControlRef = useRef<HTMLDivElement>(null);

  const config = useMemo(() => ({
    steps: [
      {
        target: sectionRef,
        from: { y: 50, opacity: 0 },
        to: { y: 0, opacity: 1, duration: 0.07 },
        position: '-=2',
      },
      {
        target: cardContainerRef,
        from: { y: 50, opacity: 0 },
        to: { y: 0, opacity: 1, duration: 2 },
        position: '<',
      },
      // 첫번째 카드 동영상 보기 버튼 활성화
      {
        target: cardsRef.current[0],
        to: { pointerEvents: 'auto' },
        position: '<',
      },
      ...[...Array.from ({ length: LIFE_EVENT_IMAGES_REVERSE.length - 1 })].flatMap((_, index) => [
        {
          target: textRefs.current[index],
          from: { opacity: TEXT_OPACITY.ACTIVE },
          to: { opacity: TEXT_OPACITY.INACTIVE, duration: 1.5 },
          position: '+=0.5',
        },
        {
          // 현재 카드 비활성화
          target: cardsRef.current[index],
          to: { opacity: 0, y: -Y_OFFSET, pointerEvents: 'none', duration: 1.5 },
          position: '<',
        },
        {
          target: textRefs.current[index+1],
          from: { opacity: TEXT_OPACITY.INACTIVE },
          to: { opacity: TEXT_OPACITY.ACTIVE, duration: 1.5 },
          position: '<',
        },
        {
          // 다음 카드 활성화
          target: cardsRef.current[index+1],
          to: { y: 0, rotate: 0, pointerEvents: 'auto', duration: 1.5 },
          position: '<',
        },
        {
          target: scrollControlRef,
          to: {
            duration: 2,
          },
          position: '>',
        },
      ]),
      {
        target: textRefs.current[LIFE_EVENT_TEXT.length-2],
        from: { opacity: TEXT_OPACITY.ACTIVE },
        to: { opacity: TEXT_OPACITY.INACTIVE, duration: 1.5 },
        position: '+=0.5',
      },
      {
        target: textRefs.current[LIFE_EVENT_TEXT.length - 1],
        from: { opacity: TEXT_OPACITY.INACTIVE },
        to: { opacity: TEXT_OPACITY.ACTIVE, duration: 1.5 },
        position: '<',
      },
      {
        target: containerRef,
        to: {
          duration: 1.5,
        },
        position: '+=1',
      },
    ],
    options: {
      start: 'top top',
      end: '+=600%',
      pin: true,
      scrub: 1,
    },
  }), []);

  useImperativeHandle(ref, () => ({
    createTimeline() {
      const tl = gsap.timeline({ defaults: { ease: 'none' } });
      for (const s of config.steps) {
        const el = s.target?.current;
        if (!el) continue;
        if (s.from) tl.fromTo(el, s.from, s.to, s.position ?? '>');
        else tl.to(el, s.to, s.position ?? '>');
      }
      return tl;
    },
  }), [config.steps]);

  return (
    <Container
      ref={containerRef}
      direction={{
        mobile: 'column',
        tablet: 'column',
        laptop: 'row',
      }}
      gap={{
        mobile: 32,
        tablet: 32,
        laptop: 130, // 84
      }}
      opacity={1}
    >
      <ScrollControl ref={scrollControlRef} />
      <Flex
        ref={sectionRef}
        direction="column"
        gap={{
          mobile: 4,
          tablet: 4,
          laptop: 12,
          desktop: 12,
        }}
        w={{
          mobile: LIFE_EVENT_IMAGE_SIZE.mobile.width,
          tablet: LIFE_EVENT_IMAGE_SIZE.tablet.width,
          laptop: 'auto',
        }}
        css={{
          position: 'relative',
          zIndex: 1,
          isolation: 'isolate',
          transform: 'translateZ(0)',
        }}
      >
        { LIFE_EVENT_TEXT.map((text, index) => (
          <StyledText key={text} ref={textRefs.current[index]}>
            { text }
          </StyledText>
        )) }
      </Flex>
      <CardContainer
        ref={cardContainerRef}
      >
        { LIFE_EVENT_IMAGES_REVERSE.map((card, index) => (
          <Card
            key={card.url}
            // NOTE: z-index로 인해 LIFE_EVENT_IMAGES_REVERSE를 사용하기때문에 실제로는 정방향 index가 reverse의 cardsLength - 1 - index와 동일
            index={cardsLength - 1 - index}
            ref={cardsRef.current[cardsLength - 1 - index]}
            {...card}
          />
        )) }
      </CardContainer>
    </Container>
  );
});

const StyledText = styled.p`
  color: #0A171F;
  font-size: 24px;
  line-height: 32px;
  font-weight: 700;
  letter-spacing: -0.4px;
  will-change: opacity;
  transform: translate3d(0, 0, 0);
  position: relative;
  z-index: 2;
  isolation: isolate;

  &:not(:first-of-type) {
    opacity: 0.1;
  }

  ${MEDIA_QUERY.min('laptop', css`
    font-size: 40px;
    line-height: 52px;
  `)}
`;

const CardContainer = styled.div`
  position: relative;
  opacity: 0;
  width: ${getRebrandStyle('lifeEventImage', 'width', 'mobile')}px;
  height: ${getRebrandStyle('lifeEventImage', 'height', 'mobile')}px;
  isolation: isolate;
  transform: translateZ(0);
  z-index: 0;

  ${MEDIA_QUERY.min('laptop', css`
    width: ${getRebrandStyle('lifeEventImage', 'containerWidth', 'laptop')}px;
    height: ${getRebrandStyle('lifeEventImage', 'height', 'laptop')}px;
  `)} 
`;
